// Dependencies
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// Components
import Container from '../../container/container';
import InputFactory from '../../forms/inputFactory/inputFactory';
import TeamMember from './teamMember/teamMember';

// Hooks
import useTeamMembersPostType from '../../../hooks/useTeamMembersPostType';

// JSON
import ambassadorLocations from '../../../json/ambassadorLocations.json';

// Styles
import TeamStyles from './team.styles';

export default function Team({ layout }) {
  const isSSR = typeof window === 'undefined';

  const filters = layout?.filters;

  const coreTeam = useTeamMembersPostType();

  const [currentFilter, setCurrentFilter] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(coreTeam);
  const [showMap, setShowMap] = useState(false);
  const [markers] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);

  const loadMultisiteAmbassadors = useCallback(async () => {
    if (markers && markers.length) return;

    const blogURL = process.env.GATSBY_BLOG_REST_URL;
    const restURL = blogURL.replace('blog/paginated', 'ambassadors/multisite');
    const response = await fetch(restURL);
    const ambassadorPosts = await response.json();
    setAmbassadors(ambassadorPosts);

    if (ambassadors && ambassadors.length) {
      ambassadors.forEach((ambassador) => {
        const location = ambassador?.location;

        if (location) {
          const filteredAmbassadorLocations = ambassadorLocations.filter(
            (ambassadorLocation) => ambassadorLocation.value === location,
          );

          if (filteredAmbassadorLocations.length) {
            const newMarker = filteredAmbassadorLocations[0];

            const filteredMarkers = markers.filter(
              (marker) => marker.value === newMarker.value,
            );
            const markerAlreadyExists = filteredMarkers.length;

            if (!markerAlreadyExists) markers.push(newMarker);
          }
        }
      });
    }
  }, [ambassadors, markers]);

  useEffect(() => {
    loadMultisiteAmbassadors();
  }, [loadMultisiteAmbassadors]);

  const handleFilterChange = (event) => {
    const value = event?.value;

    // Do nothing if filter hasn't changed
    if (value === currentFilter) return;

    // Update current filter
    setCurrentFilter(value);

    // If filter is "all", set current team to core team
    if (value === 'all') setCurrentTeam(coreTeam);
    else {
      // If filter is a location, set current team to array of members filtered by the location
      const filtered = coreTeam.filter((teamMember) =>
        teamMember?.teamOffices?.nodes.some(
          (categories) => categories?.slug === value,
        ),
      );

      setCurrentTeam(filtered);
    }
  };

  const generateFilterOptions = () => {
    const filterOptions = [];

    filterOptions.push({
      label: 'All',
      value: 'all',
    });

    if (filters?.length) {
      filters.forEach((filter) => {
        filterOptions.push({
          label: filter?.name,
          value: filter?.slug,
        });
      });
    }

    return filterOptions;
  };

  const toggleMap = () => {
    setCurrentFilter('all');
    setShowMap(!showMap);
  };

  const ClientSideOnlyAmbassadorsMap = React.lazy(
    () => import('./ambassadorsMap/ambassadorsMap'),
  );

  return (
    <TeamStyles>
      <Container>
        <header className="header">
          <h2 className="heading large">
            {showMap ? 'Meet Our Ambassadors' : 'Meet Our Experts'}
          </h2>
          <div className="controls">
            <button
              type="button"
              className="btn dark small"
              onClick={toggleMap}
            >
              {showMap ? 'Meet Our Experts' : 'Meet Our Ambassadors'}
            </button>

            {!showMap && (
              <div className="team-filter-wrap">
                <InputFactory
                  name="team_filter"
                  type="select"
                  placeholder="Filters"
                  options={generateFilterOptions()}
                  onChange={handleFilterChange}
                />
              </div>
            )}
          </div>
        </header>
        {!showMap && (
          <div className="team-members">
            {currentTeam.map((teamMember) => (
              <TeamMember teamMember={teamMember} key={teamMember?.title} />
            ))}
          </div>
        )}

        {showMap && !isSSR && (
          <React.Suspense fallback={<div />}>
            <ClientSideOnlyAmbassadorsMap
              markers={markers}
              ambassadors={ambassadors}
            />
          </React.Suspense>
        )}
      </Container>
    </TeamStyles>
  );
}

Team.propTypes = {
  layout: PropTypes.shape({
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      }),
    ),
  }),
};

Team.defaultProps = {
  layout: {},
};
